import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer } from "@mui/material";
import { findOrCreateBinAnalysis, getClient, patchClient } from "../api/paidClientsService";
import useClientData from "../hooks/useClientData";
import { DRAWER_TABS } from "../utils/constants";
import { phoneDigits, subscriptionText } from "../utils/format";
import { AccountTag, StageTag } from "./PaidClientsTable";
import { TabState } from "./common";
import PassportTab from "./tabs/PassportTab";
import SubscriptionTab from "./tabs/SubscriptionTab";
import ActivityTab from "./tabs/ActivityTab";
import GoszakupTab from "./tabs/GoszakupTab";
import NicheTab from "./tabs/NicheTab";
import WorkTab from "./tabs/WorkTab";
import UserDialogModal from "../../activeusers/components/UserDialogModal";
import UserActionsModal from "../../activeusers/components/UserActionsModal";
import SummaryDetailsModal from "../../activeusers/components/SummaryDetailsModal";
import GetLinksModal from "../../activeusers/components/GetLinksModal";

const ClientDrawer = ({ userId, tab, onTabChange, onClose, onOpenClient, onChanged, notify }) => {
  const navigate = useNavigate();
  const { data: detail, setData: setDetail, loading, error, reload } = useClientData(
    getClient,
    userId,
  );

  // Существующие модалки детализации пользователя
  const [dialogType, setDialogType] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [analysingBin, setAnalysingBin] = useState(null);

  useEffect(() => {
    setDialogType(null);
    setActionsOpen(false);
  }, [userId]);

  const activeTab = DRAWER_TABS.some((t) => t.key === tab) ? tab : "passport";

  // PATCH отвечает 204 — обновляем карточку локально: accountType лежит в корне, остальное в passport.
  // localOnly — поля только для отображения, на сервер не уходят (например managerName)
  const patch = useCallback(
    async (fields, message = "Сохранено", localOnly = {}) => {
      try {
        await patchClient(userId, fields);
        const { accountType, churnReason, ...passportFields } = fields;
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                ...(accountType !== undefined ? { accountType } : {}),
                passport: { ...(prev.passport || {}), ...passportFields, ...localOnly },
              }
            : prev,
        );
        notify(message);
        onChanged();
        return true;
      } catch (e) {
        notify(e.message);
        return false;
      }
    },
    [userId, setDetail, notify, onChanged],
  );

  const openBinDossier = async (bin) => {
    if (analysingBin) return;
    setAnalysingBin(bin);
    try {
      const id = await findOrCreateBinAnalysis(bin);
      navigate(`/active-users/${bin}/${id}`);
    } catch (e) {
      notify("Не удалось открыть досье БИН");
    } finally {
      setAnalysingBin(null);
    }
  };

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(detail.phone);
      notify("Телефон скопирован");
    } catch (e) {
      notify("Не удалось скопировать");
    }
  };

  const passport = detail?.passport || {};
  const actionDateFrom =
    detail?.subscription?.current?.startAt || passport.registeredAt || undefined;

  const tabProps = {
    userId,
    detail,
    patch,
    notify,
    reloadDetail: reload,
    onChanged,
    onOpenClient,
    goTab: onTabChange,
    openBinDossier,
    analysingBin,
    openUserDialog: setDialogType,
    openActions: () => setActionsOpen(true),
  };

  return (
    <Drawer
      anchor="right"
      open={!!userId}
      onClose={onClose}
      PaperProps={{ sx: { boxShadow: "none", background: "transparent" } }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(20,10,25,.35)" } } }}
    >
      <aside className="pc-drawer" aria-label="Карточка клиента">
        <div className="pc-dhead">
          <div className="row">
          <div>
          <h2>{detail?.displayName || (loading ? "Загрузка…" : "Имя не указано")}</h2>
          {detail && (
            <div className="pc-meta">
              {detail.phone && (
                <>
                  <span>{detail.phone}</span>
                  <button type="button" className="pc-btn sm" onClick={copyPhone} title="Скопировать">
                    📋
                  </button>
                  <a
                    className="pc-btn sm"
                    href={`https://wa.me/${phoneDigits(detail.phone)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    WhatsApp
                  </a>
                </>
              )}
              <AccountTag type={detail.accountType} />
              <StageTag stage={detail.stage} />
              <span className={`tag ${detail.daysLeft < 0 ? "acc" : ""}`}>
                {subscriptionText(detail)}
              </span>
              {passport.amoCrmUrl ? (
                <a className="tag ok" href={passport.amoCrmUrl} target="_blank" rel="noreferrer">
                  Открыть в AmoCRM ↗
                </a>
              ) : (
                <span className="tag warn">Не привязан к AmoCRM</span>
              )}
            </div>
          )}
          </div>
          <button type="button" className="pc-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
          </div>
          <div className="pc-tabs" role="tablist">
            {DRAWER_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={`pc-tab ${activeTab === t.key ? "on" : ""}`}
                onClick={() => onTabChange(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pc-dbody">
          <TabState loading={loading} error={error} data={detail}>
            {activeTab === "passport" && <PassportTab {...tabProps} />}
            {activeTab === "subscription" && <SubscriptionTab {...tabProps} />}
            {activeTab === "activity" && <ActivityTab {...tabProps} />}
            {activeTab === "goszakup" && <GoszakupTab {...tabProps} />}
            {activeTab === "niche" && <NicheTab {...tabProps} />}
            {activeTab === "work" && <WorkTab {...tabProps} />}
          </TabState>
        </div>
      </aside>

      <UserDialogModal
        open={!!dialogType}
        onClose={() => setDialogType(null)}
        userId={userId}
        type={dialogType}
        actionDateFrom={dialogType === "likes" || dialogType === "analysis" ? actionDateFrom : undefined}
        onViewResultClick={(id) => {
          setAnalysisId(id);
          setSummaryOpen(true);
        }}
        onGetLinksClick={(id) => {
          setAnalysisId(id);
          setLinksOpen(true);
        }}
      />
      <UserActionsModal
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        userId={userId}
        actionDateFrom={actionDateFrom || ""}
      />
      <SummaryDetailsModal
        open={summaryOpen}
        onClose={() => {
          setSummaryOpen(false);
          setAnalysisId(null);
        }}
        analysisId={analysisId}
      />
      <GetLinksModal
        open={linksOpen}
        onClose={() => {
          setLinksOpen(false);
          setAnalysisId(null);
        }}
        analysisId={analysisId}
      />
    </Drawer>
  );
};

export default ClientDrawer;
